import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, ArrowRight, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { SectionLabel } from "./Shared";
import { submitCareer } from "../lib/api";
import { viewportOnce } from "../lib/motion";

const inputCls =
  "w-full bg-transparent border border-white/15 rounded-sm px-4 py-3.5 font-body text-white placeholder-white/35 focus:outline-none focus:border-[#C62828] transition-colors";

export const Careers = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", expertise: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("cv", file);
      await submitCareer(fd);
      toast.success("Application received. Our team will be in touch soon.");
      setForm({ full_name: "", email: "", phone: "", expertise: "", message: "" });
      setFile(null);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="job-seekers-form" className="bg-[#1F1F1F] text-white py-24 md:py-36" data-testid="careers-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <SectionLabel dark>Careers</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight">
            Your Next Opportunity <span className="text-[#E03535]">Starts Here.</span>
          </h2>
          <p className="font-body text-white/65 text-lg leading-relaxed mt-8 max-w-lg">
            Whether you are taking your first career step or looking for your next leadership opportunity,
            Prospera Staffing helps connect professionals with organizations where they can grow.
          </p>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7 }}
          data-testid="careers-form"
          className="space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="career-name" className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} />
            <input data-testid="career-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="career-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            <input data-testid="career-expertise" className={inputCls} placeholder="Area of Expertise" value={form.expertise} onChange={set("expertise")} />
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            data-testid="career-upload"
            className="flex items-center gap-3 border border-dashed border-white/25 rounded-sm px-4 py-4 cursor-pointer hover:border-[#C62828] transition-colors"
          >
            {file ? <FileText size={18} className="text-[#E03535]" /> : <Upload size={18} className="text-white/50" />}
            <span className="font-body text-sm text-white/60 truncate">{file ? file.name : "Upload your CV (PDF, DOC — max 5MB)"}</span>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <textarea data-testid="career-message" rows={4} className={inputCls} placeholder="Message (optional)" value={form.message} onChange={set("message")} />

          <button
            type="submit"
            disabled={loading}
            data-testid="career-submit"
            className="group inline-flex items-center gap-2 bg-[#C62828] text-white font-accent uppercase tracking-widest text-sm px-8 py-4 rounded-sm hover:bg-white hover:text-[#111111] transition-colors duration-400 disabled:opacity-60"
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <>Submit Your CV <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Careers;
